// תיעוד API לסנכרון לידים — מוצג בלשונית "API" בהגדרות.
// כל section: title + intro (טקסט חופשי) + steps (שלבים ממוספרים, אופציונלי)
// + code (בלוק קוד להעתקה, אופציונלי) — כדי שהרכיב יעצב יפה, לא רק <pre>.

export const LEAD_API_BASE = 'https://api.kailai.tech/api/v1/merkaziya/v1';

export interface DocSection {
  id: string;
  title: string;
  icon: string;          // lucide icon name
  intro: string;         // פסקת הסבר קצרה
  steps?: string[];      // שלבים ממוספרים
  code?: { label: string; lang: 'bash' | 'json' | 'text'; body: string }[];
  tip?: string;          // טיפ מודגש בסוף
}

export const LEAD_API_DOCS: DocSection[] = [
  {
    id: 'auth',
    title: 'שלב 1 · אימות — מפתח API',
    icon: 'key',
    intro:
      'כל בקשה ל-API דורשת מפתח. צור מפתח למעלה בעמוד הזה (כפתור "מפתח חדש") עם ההרשאות leads.read ו-leads.write, והעבר אותו בכותרת X-Api-Key.',
    code: [
      {
        label: 'כותרות לכל בקשה',
        lang: 'text',
        body: `X-Api-Key: mrk_xxxxxxxxxxxxxxxxxxxx
Content-Type: application/json`,
      },
      {
        label: 'בדיקת חיבור מהירה (curl)',
        lang: 'bash',
        body: `curl -H "X-Api-Key: המפתח-שלך" \\
  ${LEAD_API_BASE}/me`,
      },
    ],
    tip: 'המפתח מוצג פעם אחת בלבד ביצירה — שמור אותו במקום בטוח.',
  },
  {
    id: 'push',
    title: 'שלב 2 · דחיפת ליד (מטופס, Make, n8n)',
    icon: 'download',
    intro:
      'POST /leads קולט ליד חדש או מעדכן קיים (upsert): אם שלחת external_id — הזיהוי לפיו; אחרת לפי טלפון. אותו ליד לעולם לא נכפל.',
    code: [
      {
        label: 'ליד גלובלי (נכנס למאגר אנשי הקשר)',
        lang: 'bash',
        body: `curl -X POST ${LEAD_API_BASE}/leads \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{
    "external_id": "crm-1024",
    "phone": "0501234567",
    "name": "ישראל ישראלי",
    "email": "israel@example.com",
    "status": "new",
    "source": "facebook-form",
    "fields": { "תקציב": "5000", "עיר": "חיפה" }
  }'`,
      },
      {
        label: 'ליד ישירות לקמפיין חיוג — לפי שם הקמפיין',
        lang: 'bash',
        body: `curl -X POST ${LEAD_API_BASE}/leads \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{
    "phone": "0501234567",
    "name": "ישראל ישראלי",
    "campaign": "קמפיין קיץ"
  }'
# campaign = שם הקמפיין (לא רגיש לאותיות גדולות/קטנות). לחלופין campaign_id: 12.
# הליד נכנס גם למאגר וגם לתור החיוג של הקמפיין (סטטוס queued).
# לדחיפה חוזרת של אותו טלפון לאותו קמפיין — לא נוצר כפול.
# עובד גם ב-batch: הוסף "campaign" לכל אובייקט במערך.`,
      },
      {
        label: 'תשובה',
        lang: 'json',
        body: `{
  "success": true,
  "data": {
    "id": 8, "external_id": "crm-1024",
    "phone": "0501234567", "name": "ישראל ישראלי",
    "status": "new", "source": "facebook-form",
    "campaign_lead_id": 341     // רק כששלחת campaign_id
  }
}
// 201 = ליד חדש, 200 = ליד קיים עודכן`,
      },
      {
        label: 'כמה לידים בבת אחת (ללא הגבלת כמות) — שלח מערך',
        lang: 'bash',
        body: `curl -X POST ${LEAD_API_BASE}/leads \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '[
    { "external_id": "crm-1", "phone": "0501111111", "name": "ראשון" },
    { "external_id": "crm-2", "phone": "0502222222", "name": "שני" },
    { "phone": "0503333333", "campaign": "קמפיין קיץ" }
  ]'
# אפשר גם עטוף: { "leads": [ {…}, {…} ] }
# כל ליד עובר upsert בנפרד — שורה שגויה נכשלת לבד ולא חוסמת את השאר.
# ניתן לצרף "campaign" (שם) או "campaign_id" לכל ליד — גם המוני.`,
      },
      {
        label: 'תשובת מערך (batch)',
        lang: 'json',
        body: `{
  "success": true,                       // false אם חלק נכשלו
  "summary": { "total": 3, "created": 2, "updated": 1, "failed": 0 },
  "data": [
    { "index": 0, "ok": true, "created": true, "lead": { "id": 8, … } },
    { "index": 1, "ok": true, "created": true, "lead": { … } },
    { "index": 2, "ok": false, "error": "מספר טלפון לא תקין" }
  ]
}
// index = מיקום הליד במערך ששלחת; קרא ok/error לכל שורה כדי לזהות כשלים חלקיים.`,
      },
    ],
    tip: 'מזהה חיצוני (external_id) הוא הדרך המומלצת — כך תוכל לעדכן את הליד אחר-כך לפי המזהה של המערכת שלך. לייבוא המוני שלח מערך של לידים בבקשה אחת — ללא הגבלת כמות.',
  },
  {
    id: 'update',
    title: 'שלב 3 · עדכון ליד (סטטוס, שדות) מכל מערכת',
    icon: 'refresh-cw',
    intro:
      'PATCH /leads/{id} מעדכן ליד קיים. אפשר לפנות לפי ה-id הפנימי או לפי ה-external_id שלך — שלח רק את השדות שהשתנו.',
    code: [
      {
        label: 'שינוי סטטוס לפי המזהה החיצוני שלך',
        lang: 'bash',
        body: `curl -X PATCH ${LEAD_API_BASE}/leads/crm-1024 \\
  -H "X-Api-Key: המפתח-שלך" \\
  -H "X-Sync-Source: my-crm" \\
  -H "Content-Type: application/json" \\
  -d '{ "status": "qualified", "fields": { "הערה": "ביקש הצעת מחיר" } }'`,
      },
      {
        label: 'הוספת/עדכון שדה בודד — בלי לדרוס את השאר (מיזוג)',
        lang: 'bash',
        body: `# fields תמיד מתמזג: שולחים רק את מה שמוסיפים/משנים —
# כל שאר השדות הקיימים על הליד נשמרים.
curl -X PATCH ${LEAD_API_BASE}/leads/crm-1024 \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{ "fields": { "מקור": "פייסבוק" } }'

# מחיקת שדה: שלח אותו עם null
curl -X PATCH ${LEAD_API_BASE}/leads/crm-1024 \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{ "fields": { "עיר": null } }'

# החלפה מלאה של כל השדות (סנכרון-מלא): fields_replace=true
curl -X PATCH ${LEAD_API_BASE}/leads/crm-1024 \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{ "fields": { "תקציב": "8000" }, "fields_replace": true }'

# אותו מיזוג חל גם ב-POST /leads (upsert) על ליד קיים.`,
      },
    ],
    tip: 'הכותרת X-Sync-Source חשובה! היא מסמנת מי ביצע את העדכון — וכך ה-webhook היוצא לא "יהדהד" את העדכון בחזרה למערכת ששלחה אותו (מניעת לולאות סנכרון).',
  },
  {
    id: 'notes',
    title: 'הערות ושדות מותאמים — גלובלי או לקמפיין ספציפי',
    icon: 'doc',
    intro:
      'אפשר לצרף הערות ושדות בשתי רמות: גלובלית על הליד עצמו (note / fields — מופיעות בכרטיס הלקוח), או צמודות לליד בתוך קמפיין ספציפי (campaign_note / campaign_fields — מופיעות לנציג המחייג בקמפיין). אפשר לשלוח אותן יחד עם הליד, או בנפרד — בלי לשלוח את הליד מחדש.',
    code: [
      {
        label: 'יחד עם הליד — הערה גלובלית + הערה ושדות לקמפיין',
        lang: 'bash',
        body: `curl -X POST ${LEAD_API_BASE}/leads \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{
    "phone": "0501234567", "name": "ישראל",
    "note": "התעניין במסלול פרימיום",
    "fields": { "עיר": "חיפה" },
    "campaign": "קמפיין קיץ",
    "campaign_note": "לחזור אחרי 17:00",
    "campaign_fields": { "תקציב": "5000" }
  }'
# note / fields   → על הליד (גלובלי, לכל הקמפיינים)
# campaign_note / campaign_fields → רק בקמפיין שצוין`,
      },
      {
        label: 'בלי הליד — הערה לליד קיים (לפי טלפון / external_id / id)',
        lang: 'bash',
        body: `# הערה גלובלית:
curl -X POST ${LEAD_API_BASE}/leads/0501234567/notes \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{ "body": "עדכון מהמערכת החיצונית" }'

# הערה בקמפיין ספציפי (הליד חייב להיות בקמפיין):
curl -X POST ${LEAD_API_BASE}/leads/crm-1024/notes \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{ "body": "לחזור מחר", "campaign": "קמפיין קיץ" }'

# לא נוצר ליד חדש — אם הליד לא קיים תתקבל 404.`,
      },
      {
        label: 'בלי הליד — עדכון שדות קמפיין דרך PATCH',
        lang: 'bash',
        body: `curl -X PATCH ${LEAD_API_BASE}/leads/crm-1024 \\
  -H "X-Api-Key: המפתח-שלך" -H "Content-Type: application/json" \\
  -d '{ "campaign": "קמפיין קיץ", "campaign_fields": { "חבילה": "2" } }'`,
      },
      {
        label: 'קריאת כל ההערות של ליד',
        lang: 'bash',
        body: `curl -H "X-Api-Key: המפתח-שלך" \\
  ${LEAD_API_BASE}/leads/crm-1024/notes

# מחזיר notes (גלובליות) + campaign_notes (לפי קמפיין)`,
      },
    ],
    tip: 'הערות שנוספות דרך ה-API משדרות אירוע lead.note_added — אפשר לחבר אליו Webhook או אוטומציה.',
  },
  {
    id: 'pull',
    title: 'שלב 4 · משיכת לידים',
    icon: 'upload',
    intro: 'GET /leads מחזיר לידים עם סינון — נוח ל-polling תקופתי או למשיכת שינויים.',
    code: [
      {
        label: 'כל הלידים שעודכנו מאז זמן מסוים',
        lang: 'bash',
        body: `curl -H "X-Api-Key: המפתח-שלך" \\
  "${LEAD_API_BASE}/leads?since=2026-07-01T00:00:00Z&per_page=50"

# פרמטרים: status=new | external_id=crm-1024 | since=ISO | per_page (עד 100)`,
      },
    ],
  },
  {
    id: 'webhook',
    title: 'שלב 5 · קבלת עדכונים אוטומטית (Webhook יוצא)',
    icon: 'zap',
    intro:
      'במקום למשוך — קבל דחיפה בזמן אמת. צור Webhook בלשונית Webhooks ובחר את האירועים. אפשר להגביל אותו לקמפיין מסוים ו/או לסטטוסים מסוימים — או להשאיר גלובלי.',
    steps: [
      'לשונית Webhooks → "Webhook חדש" → הדבק את ה-URL שלך (מ-Make/n8n).',
      'בחר אירועים: lead.created / lead.updated / lead.status_changed, או אירועי שיחות (call.ended וכו׳).',
      'טווח: גלובלי (הכל) או פר-קמפיין — ואז גם אפשר לבחור רק סטטוסים/תיוגים מסוימים.',
      'שמור את ה-secret ואמת את חתימת X-Kali-Signature (HMAC-SHA256) בצד שלך.',
    ],
    code: [
      {
        label: 'דוגמת payload של lead.status_changed',
        lang: 'json',
        body: `{
  "event": "lead.status_changed",
  "lead_id": 341,
  "campaign_id": 12,
  "campaign_name": "קמפיין קיץ",
  "phone": "0501234567",
  "name": "ישראל ישראלי",
  "disposition": "deal_closed",
  "old_status": "calling",
  "new_status": "completed",
  "agent_name": "שירה",
  "_sync_source": null
}`,
      },
    ],
    tip: 'בדוק את השדה _sync_source: אם הוא שווה לשם שהמערכת שלך שולחת ב-X-Sync-Source — דלג על העדכון (זה שינוי שאתה יצרת).',
  },
  {
    id: 'make',
    title: 'מדריך מלא · Make (Integromat)',
    icon: 'workflow',
    intro: 'חיבור דו-כיווני מלא בין Make למרכזיה בשני תרחישים.',
    steps: [
      'תרחיש א — ליד מ-Make למרכזיה: הוסף מודול "HTTP → Make a request", Method: POST, URL כמו בדוגמה, Headers: X-Api-Key + Content-Type, ומפה את השדות ל-Body.',
      'תרחיש ב — עדכון ממרכזיה ל-Make: צור "Webhooks → Custom webhook" ב-Make, העתק את הכתובת, וצור איתה Webhook במרכזיה עם האירוע lead.status_changed.',
      'הוסף Filter אחרי ה-webhook ב-Make: המשך רק אם _sync_source ≠ "make" (מניעת לולאה).',
    ],
    code: [
      {
        label: 'הגדרות המודול ב-Make',
        lang: 'text',
        body: `URL:     ${LEAD_API_BASE}/leads
Method:  POST
Headers: X-Api-Key = המפתח שלך
         Content-Type = application/json
         X-Sync-Source = make
Body:    { "external_id": "{{id}}", "phone": "{{phone}}",
           "name": "{{name}}", "campaign_id": 12 }`,
      },
    ],
  },
  {
    id: 'n8n',
    title: 'מדריך מלא · n8n',
    icon: 'git-branch',
    intro: 'אותו חיבור דו-כיווני עם צמתי n8n.',
    steps: [
      'קליטה: צומת "HTTP Request" — POST לכתובת ה-leads, Authentication: Header Auth (X-Api-Key), JSON Body עם שדות הליד.',
      'עדכון סטטוס: PATCH ל-/leads/{{$json.external_id}} עם כותרת X-Sync-Source: n8n.',
      'קבלה: צומת "Webhook" (Production URL) → הזן אותו במרכזיה בלשונית Webhooks.',
      'מניעת לולאה: צומת IF — אם {{$json.body._sync_source}} == "n8n" → עצור.',
    ],
    code: [
      {
        label: 'צומת HTTP Request (קליטת ליד)',
        lang: 'text',
        body: `Method:  POST
URL:     ${LEAD_API_BASE}/leads
Auth:    Header Auth → Name: X-Api-Key, Value: המפתח
Headers: X-Sync-Source: n8n
Body (JSON):
{ "external_id": "{{ $json.id }}",
  "phone": "{{ $json.phone }}",
  "status": "new" }`,
      },
    ],
  },
];
